import { useEffect, useState, useCallback, useRef } from 'react';
import ApiDatabase from '../../../../../../server';

// Types de documents à récupérer
const DOCUMENT_TYPES_TO_FETCH = [
  { type: 'identity', label: "Pièce d'identité" },
  { type: 'selfie', label: 'Selfie' },
  { type: 'visa', label: 'Visa / Titre de séjour' },
  { type: 'card_vitality', label: 'Carte Vitale' },
  { type: 'proof_of_address', label: 'Justificatif de domicile' },
  { type: 'accommodation_of_address', label: "Attestation d'hébergement" },
  { type: 'hostIdentity', label: "Pièce d'identité de l'hôte" },
];

// Cache global pour les documents (persiste entre les montages/démontages)
// Structure: { [adminUserId]: { documents: [], timestamp: Date.now(), complete: boolean } }
const documentsCache = {};
const inFlightFetches = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Parse les URLs de documents retournés par l'API
 */
const parseDocUrls = (data) => {
  const urls = Array.isArray(data?.urls) ? data.urls : [];
  return urls.filter(Boolean).map((item) => {
    const href = item.url || item.uri || '';
    let originalFileName = '';
    try {
      const u = new URL(href);
      originalFileName = decodeURIComponent(
        (u.pathname.split('/').pop() || '').trim()
      );
    } catch (e) {
      const clean = href.split('?')[0].split('#')[0];
      originalFileName = decodeURIComponent(
        (clean.split('/').pop() || '').trim()
      );
    }
    return {
      uri: href,
      fileName: originalFileName || 'document',
      fileType: item.type || 'application/pdf',
    };
  });
};

/**
 * Vérifie si le cache est valide et complet pour un utilisateur
 */
const isCacheValid = (adminUserId) => {
  const cached = documentsCache[adminUserId];
  if (!cached) return false;
  if (!cached.complete) return false;
  return Date.now() - cached.timestamp < CACHE_DURATION;
};

const getCachedDocuments = (adminUserId) =>
  isCacheValid(adminUserId) ? documentsCache[adminUserId].documents : [];

const fetchDocumentType = ({ token, adminUserId, type, label }) =>
  new Promise((resolve) => {
    ApiDatabase.postPresignFiles(
      {
        token,
        idUser: adminUserId,
        type,
        fileName: '',
      },
      (data) => {
        const docs = parseDocUrls(data);
        resolve(
          docs.length > 0
            ? { type, label, docs }
            : null
        );
      },
      (err) => {
        if (err?.name !== 'CanceledError') {
          console.log(`Error fetching ${type}:`, err);
        }
        resolve(null);
      },
      {
        abortKey: `admin/presignFiles/all/${adminUserId}/${type}`,
      }
    );
  });

const loadAllDocumentsForUser = async (token, adminUserId) => {
  const results = await Promise.all(
    DOCUMENT_TYPES_TO_FETCH.map(({ type, label }) =>
      fetchDocumentType({ token, adminUserId, type, label })
    )
  );

  const documents = results.filter(Boolean);
  documentsCache[adminUserId] = {
    documents,
    timestamp: Date.now(),
    complete: true,
  };
  return documents;
};

/**
 * Hook pour récupérer tous les documents d'un utilisateur
 * Les documents sont mis en cache pour éviter les rechargements inutiles.
 *
 * @param {object} params
 * @param {string} params.token - Token d'authentification
 * @param {string} params.adminUserId - ID de l'utilisateur
 * @param {string} params.currentType - Type de document actuellement affiché (exclu de la liste)
 * @param {boolean} params.enabled - Activer le chargement
 * @returns {object} { allDocuments, isLoading }
 */
const useAllUserDocuments = ({
  token,
  adminUserId,
  currentType = null,
  enabled = true,
}) => {
  const [allDocuments, setAllDocuments] = useState(() =>
    getCachedDocuments(adminUserId)
  );
  const [isLoading, setIsLoading] = useState(
    () => enabled && !!adminUserId && !isCacheValid(adminUserId)
  );
  const isMountedRef = useRef(true);
  const hasDocumentsRef = useRef(allDocuments.length > 0);

  useEffect(() => {
    hasDocumentsRef.current = allDocuments.length > 0;
  }, [allDocuments.length]);

  const fetchAllDocuments = useCallback(async () => {
    if (!adminUserId || !token) {
      setAllDocuments([]);
      setIsLoading(false);
      return;
    }

    if (isCacheValid(adminUserId)) {
      setAllDocuments(documentsCache[adminUserId].documents);
      setIsLoading(false);
      return;
    }

    if (inFlightFetches[adminUserId]) {
      try {
        await inFlightFetches[adminUserId];
        if (isMountedRef.current) {
          setAllDocuments(getCachedDocuments(adminUserId));
          setIsLoading(false);
        }
      } catch {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
      return;
    }

    const hasCachedData = hasDocumentsRef.current;
    if (!hasCachedData) {
      setIsLoading(true);
    }

    inFlightFetches[adminUserId] = loadAllDocumentsForUser(token, adminUserId);

    try {
      const documents = await inFlightFetches[adminUserId];
      if (isMountedRef.current) {
        setAllDocuments(documents);
        setIsLoading(false);
      }
    } catch (err) {
      console.log('Error fetching all documents:', err);
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    } finally {
      delete inFlightFetches[adminUserId];
    }
  }, [token, adminUserId]);

  const filteredDocuments = allDocuments.filter((d) => d.type !== currentType);

  const invalidateAndRefetch = useCallback(() => {
    if (adminUserId) {
      delete documentsCache[adminUserId];
    }
    fetchAllDocuments();
  }, [adminUserId, fetchAllDocuments]);

  useEffect(() => {
    isMountedRef.current = true;

    if (enabled) {
      fetchAllDocuments();
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [enabled, fetchAllDocuments]);

  useEffect(() => {
    if (!adminUserId) {
      setAllDocuments([]);
      setIsLoading(false);
      return;
    }

    const cached = getCachedDocuments(adminUserId);
    if (cached.length > 0) {
      setAllDocuments(cached);
      setIsLoading(false);
    }
  }, [adminUserId]);

  return {
    allDocuments: filteredDocuments,
    isLoading,
    refetch: fetchAllDocuments,
    invalidateAndRefetch,
  };
};

export default useAllUserDocuments;
